import React, { PureComponent } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { LocationDto, DropDownLocationsStore } from '../../stores/configuration/locations/locations-store';
import { nameof } from '../../utils/object';
import { OrderDefinition } from 'stores/dataStore';
import TailwindSelect from 'widgets/editors/tailwind-select';

interface TailwindLocationEditorWithRoleLocationsProps extends WithTranslation {
  value: string;
  onChange: (value: LocationDto) => void;
  placeholder?: string;
  locationsStore?: DropDownLocationsStore;
  locationsReceived?: string[];
  clearable?: boolean;
  className?: string;
  showCountry?: boolean;
  customPlaceholder?: string;
  extraLocationOptions?: any[];
  loadDataOnOpen?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

interface TailwindLocationEditorWithRoleLocationsState {
  value: string;
  availableLocations: { [id: string]: LocationDto };
  locationOptions: { text: string; value: string }[];
  isSelected: boolean;
  isLoading: boolean;
}

@connect(['locationsStore', DropDownLocationsStore])
class TailwindLocationEditorWithRoleLocations extends PureComponent<
  TailwindLocationEditorWithRoleLocationsProps,
  TailwindLocationEditorWithRoleLocationsState
> {
  state: TailwindLocationEditorWithRoleLocationsState = {
    value: this.props.value,
    availableLocations: {},
    locationOptions: [],
    isSelected: false,
    isLoading: false
  };

  componentDidMount() {
    if (!this.props.loadDataOnOpen || (this.state.value && this.state.locationOptions.length === 0)) {
      this.initLocations();
    }
  }

  componentDidUpdate({ value: prevValue }: TailwindLocationEditorWithRoleLocationsProps) {
    const { value } = this.props;
    if (value !== prevValue) this.setState({ value }, this.initLocations);
  }

  onOpenDropDown = () => {
    if (this.props.loadDataOnOpen && this.state.locationOptions.length === 0) {
      this.initLocations();
    }
  };

  initLocations = async () => {
    this.setState({ isLoading: true });
    const { locationsReceived, locationsStore } = this.props;
    let filter;
    if ((locationsReceived || []).length > 0) filter = [{ id: { in: { type: 'guid', value: locationsReceived } } }];
    else filter = [];

    const orderBy: OrderDefinition[] = [{ direction: 'Ascending', field: nameof<LocationDto>('location'), useProfile: false }];
    const locations = await locationsStore.getAllAsync({ searchQuery: '', skip: 0, take: 100000, orderBy, filter }, locationsReceived);
    const { availableLocations, locationOptions } = this.setAvailableLocations(locations?.items || []);
    this.setState({ availableLocations, locationOptions, isLoading: false });
  };

  setAvailableLocations = (locations: LocationDto[] = []) => {
    const { locationsReceived, extraLocationOptions } = this.props;
    let availableLocations: { [id: string]: LocationDto } = {};
    let locationOptions = [];
    if ((locationsReceived || []).length <= 0) {
      availableLocations = (locations || []).reduce((acc, curr) => {
        acc[curr?.id] = curr;
        return acc;
      }, {});
      locationOptions = (locations || []).map(({ location, id }) => ({ text: location, value: id }));
    } else
      locations.forEach(location => {
        const target = (locationsReceived || []).find(id => id === location.id);
        if (target) {
          availableLocations[target] = location;
          if (!this.props.showCountry || location.code === 'XXXX' || location.code === 'XXXXX' || !location.location) {
            locationOptions.push({ text: location.location, value: location.id });
          } else {
            locationOptions.push({ text: location.location + ' - ' + location.countryName, value: location.id });
          }
        }
      });
    if (!!extraLocationOptions && extraLocationOptions.length > 0) {
      let aux = [];
      extraLocationOptions.forEach(x => {
        aux.push(x);
        aux = aux.concat(locationOptions);
        locationOptions = aux;

        availableLocations[x.value] = {
          id: x.id,
          location: x.text,
          code: '',
          countryId: '',
          countryName: '',
          isoCode: '',
          active: true,
          region: '',
          regionId: '',
          createdOn: '',
          modifiedOn: '',
          modifiedByUserId: '',
          modifiedByUser: ''
        };
      });
    }
    return { availableLocations, locationOptions };
  };

  selectedLocation = (value: string) => {
    this.setState({ value, isSelected: !!value });
    const loc = this.state.availableLocations[value];
    this.props.onChange(loc);
  };

  public render() {
    const { t, locationsStore, customPlaceholder } = this.props;
    const { value } = this.state;
    const { locationOptions } = this.setAvailableLocations((locationsStore.state.items || []).map(({ item }) => item) || []);

    return (
      <>
        <TailwindSelect
          options={locationOptions.map(opt => ({
            id: opt.value,
            text: opt.text
          }))}
          value={value ? value : null}
          onChange={value => this.selectedLocation(value as string)}
          placeholder={customPlaceholder ? t(customPlaceholder) : t('Select location')}
          clearable
          searchable
          className="w-full"
          onOpenDropDown={this.onOpenDropDown}
        />
      </>
    );
  }
}

export default withTranslation()(TailwindLocationEditorWithRoleLocations);
